export type PrimaryItem = { skuId: string; warehouseId: string; quantity: number }
type PreorderRow = {
  skuId: string
  preordersBeforeEAD?: number
  preordersPurchasePeriod?: number
  suggestedOrderRawQuantity?: number
}
export type UnfulfillableSyncRow = {
  skuId: string
  suggestedOrderRawQuantity?: number
  unfulfillableBeforeEAD?: number
  atRiskBeforeEAD?: number
}
/**
 * Syncs order quantities to preorder data.
 *
 * PurchasePeriod: quantity = preordersPurchasePeriod.
 * BeforeEAD: quantity = preordersBeforeEAD + suggestedOrderRawQuantity (both default to 0).
 * SKUs missing from rowData get quantity 0.
 * Unchanged quantities return the same object reference.
 */
export function syncToPreorders(
  primaryItems: PrimaryItem[],
  rowData: PreorderRow[],
  type: "BeforeEAD" | "PurchasePeriod",
): PrimaryItem[] {
  const rowMap = new Map(rowData.map((row) => [row.skuId, row]))
  return primaryItems.map((item) => {
    const sku = rowMap.get(item.skuId)

    let newQuantity: number
    switch (type) {
      case "BeforeEAD":
        newQuantity = (sku?.preordersBeforeEAD ?? 0) + (sku?.suggestedOrderRawQuantity ?? 0)
        break
      case "PurchasePeriod":
        newQuantity = sku?.preordersPurchasePeriod ?? 0
        break
    }

    return newQuantity !== item.quantity ? { ...item, quantity: newQuantity } : item
  })
}

/**
 * Syncs order quantities to the suggested order plus unfulfillable (and optionally
 * at-risk) pre-orders before ETA. Used by the "Add unfulfillable pre-orders" sync options.
 *
 * quantity = suggestedOrderRawQuantity + unfulfillableBeforeEAD (+ atRiskBeforeEAD when "both").
 * The existing quantity is replaced, not added to. All fields default to 0; SKUs missing
 * from rowData get quantity 0. Unchanged quantities return the same object reference.
 */
export function syncToUnfulfillablePreorders(
  primaryItems: PrimaryItem[],
  rowData: UnfulfillableSyncRow[],
  type: "unfulfillable" | "both",
): PrimaryItem[] {
  const rowMap = new Map(rowData.map((row) => [row.skuId, row]))
  return primaryItems.map((item) => {
    const sku = rowMap.get(item.skuId)
    const add =
      (sku?.unfulfillableBeforeEAD ?? 0) + (type === "both" ? (sku?.atRiskBeforeEAD ?? 0) : 0)
    const newQuantity = (sku?.suggestedOrderRawQuantity ?? 0) + add
    return newQuantity !== item.quantity ? { ...item, quantity: newQuantity } : item
  })
}
